import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sword,
  Calendar,
  Trophy,
  TrendingUp,
  Shield,
  Sparkles,
  ArrowRight,
  Activity,
  Clock,
  Users, Medal,
  Film, PartyPopper,
} from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import AdminProofReviewModal from '../components/modals/AdminProofReviewModal';
import { LoadingPage } from '../components/ui/Loading';
import api from '../services/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showProofReviewModal, setShowProofReviewModal] = useState(false);

  const isAdmin = user?.role === 'ADMIN';

  // Verificar se o botão de Recap 2025 deve ser exibido (até 05/01/2026)
  const recapDeadline = new Date('2026-01-05T23:59:59');
  const showRecapButton = new Date() <= recapDeadline;
  const birthdayDeadline = new Date('2026-01-31T23:59:59');
  const showBirthdayButton = new Date() <= birthdayDeadline;

  // Função para redirecionar ao recap do char ativo de maior nível
  const goToRecap = async () => {
    try {
      const response = await api.get('/characters/me');
      const userCharacters = response.data.characters || [];
      
      // Filtrar apenas chars ativos e ordenar por nível (maior para menor)
      const activeChars = userCharacters
        .filter(char => char.isActive)
        .sort((a, b) => b.level - a.level);
      
      if (activeChars.length > 0) {
        // Redirecionar para o char de maior nível
        navigate(`/app/recap/${activeChars[0].id}`);
      } else {
        // Se não houver chars ativos, redirecionar para seleção
        navigate('/app/recap');
      }
    } catch (err) {
      console.error('Erro ao buscar personagens:', err);
      // Em caso de erro, redirecionar para seleção
      navigate('/app/recap');
    }
  };

  // Estados para dados dinâmicos
  const [stats, setStats] = useState([]);
  const [statsAdmin, setStatsAdmin] = useState([]);
  const [quickActions] = useState([
    {
      name: 'Criar Personagem',
      description: 'Adicione um novo personagem ao seu clã',
      icon: Sparkles,
      color: 'from-cyan-500 to-blue-600',
      href: '/app/characters'
    },
    {
      name: 'Ver Eventos',
      description: 'Participe dos próximos eventos',
      icon: Calendar,
      color: 'from-purple-500 to-pink-600',
      href: '/app/events'
    },
    {
      name: 'Ranking',
      description: 'Veja sua posição no ranking',
      icon: Trophy,
      color: 'from-amber-500 to-orange-600',
      href: '/app/ranking'
    }
  ]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar dados da dashboard
  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isAdmin) {
          // Dados para ADMIN - Estatísticas do clã
          const [charactersRes, eventsRes, pendingUsersRes, usersQuantityRes] = await Promise.all([
            api.get('/characters?limit=9999'),
            api.get('/events'),
            api.get('/admin/users/pending'),
            api.get('/admin/users/quantity')
          ]);

          const characters = charactersRes.data.characters || [];
          const events = eventsRes.data.events || [];
          const pendingUsers = pendingUsersRes.data.users || [];
          const usersQuantity = usersQuantityRes.data.users || 0;

          const completeEvents = events.filter(e => e.status === 'COMPLETED').length;
          const scheduledEvents = events.filter(e => e.status === 'SCHEDULED').length;

          const activeCharacters = characters.filter(e => e.status === 'ACTIVE');

          // Calcular estatísticas do clã
          const totalCharacters = activeCharacters.length;
          const chars165 = activeCharacters.reduce((sum, char) => sum + (char.level >= 165 ? 1 : 0), 0);
          const chars172 = activeCharacters.reduce((sum, char) => sum + (char.level >= 172 ? 1 : 0), 0);
          const avgLevel = activeCharacters.length > 0
            ? Math.round(activeCharacters.reduce((sum, char) => sum + (char.level || 0), 0) / activeCharacters.length)
            : 0;

          const adminStats = [
            {
              name: 'Total de Personagens',
              value: totalCharacters.toString(),
              change: `${activeCharacters.filter(c => {
                const created = new Date(c.createdAt);
                const date = new Date();
                const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                return created > firstDay;
              }).length} este mês`,
              changeType: 'positive',
              icon: Sword,
              color: 'from-cyan-500 to-blue-600',
              href: '/app/characters'
            },
            {
              name: 'Eventos Agendados',
              value: scheduledEvents.toString(),
              change: `${completeEvents} completos`,
              changeType: 'positive',
              icon: Calendar,
              color: 'from-purple-500 to-pink-600',
              href: '/app/events'
            },
            {
              name: 'Nível médio do Clã',
              value: avgLevel.toString(),
              change: `165+: ${chars165} | 172+: ${chars172}`,
              changeType: 'positive',
              icon: Trophy,
              color: 'from-amber-500 to-orange-600',
              href: '/app/ranking'
            },
            {
              name: 'Membros Ativos',
              value: usersQuantity.toString(),
              change: 'No ranking',
              changeType: 'positive',
              icon: Users,
              color: 'from-emerald-500 to-teal-600',
              href: '/app/ranking'
            },
            {
              name: 'Recrutamentos Pendentes',
              value: pendingUsers.length.toString(),
              change: pendingUsers.length > 0 ? 'Aguardando análise' : 'Nenhum pendente',
              changeType: pendingUsers.length > 0 ? 'neutral' : 'positive',
              icon: Medal,
              color: 'from-rose-500 to-pink-600',
              href: '/app/admin/enlistments'
            }
          ];

          setStatsAdmin(adminStats);
        }

        // Dados para MEMBRO - Estatísticas pessoais
        const [charactersRes, myStatsRes, rankingRes] = await Promise.all([
          api.get('/characters/me'),
          api.get('/events/my-stats'),
          api.get('/gamification/ranking')
        ]);

        const userCharacters = charactersRes.data.characters || [];
        const myStats = myStatsRes.data;
        const ranking = rankingRes.data.ranking || rankingRes.data.characters || [];

        // Calcular estatísticas pessoais
        const totalPoints = userCharacters.reduce((sum, char) => sum + (char.totalPoints || 0), 0);
        const highestLevel = userCharacters.length > 0
          ? Math.max(...userCharacters.map(c => c.level || 0))
          : 0;

        // Encontrar a melhor posição no ranking (personagem do usuário com melhor colocação)
        let userPosition = 0;
        if (userCharacters.length > 0 && ranking.length > 0) {
          const positions = userCharacters
            .map(uc => {
              const pos = ranking.findIndex(rc => rc.id === uc.id);
              return pos >= 0 ? pos + 1 : 0;
            })
            .filter(pos => pos > 0);

          userPosition = positions.length > 0 ? Math.min(...positions) : 0;
        }

        const memberStats = [
          {
            name: 'Meus Personagens',
            value: userCharacters.length.toString(),
            change: `Nível máximo: ${highestLevel}`,
            changeType: 'positive',
            icon: Sword,
            color: 'from-cyan-500 to-blue-600',
            href: '/app/characters'
          },
          {
            name: 'Eventos Participados',
            value: (myStats.participatedEvents ?? 0).toString(),
            change: `${myStats.scheduledEvents ?? 0} disponíveis`,
            changeType: 'positive',
            icon: Calendar,
            color: 'from-purple-500 to-pink-600',
            href: '/app/events'
          },
          {
            name: 'Meus Pontos',
            value: totalPoints.toLocaleString('pt-BR'),
            change: 'Total acumulado',
            changeType: 'positive',
            icon: Trophy,
            color: 'from-amber-500 to-orange-600',
            href: '/app/ranking'
          },
          {
            name: 'Posição no Ranking',
            value: userPosition > 0 ? `#${userPosition}` : (userCharacters.length > 0 ? '--' : 'N/A'),
            change: userPosition > 0
              ? `De ${ranking.length} personagens`
              : (userCharacters.length > 0 ? 'Participe de eventos' : 'Crie um personagem'),
            changeType: userPosition > 0 ? 'positive' : 'neutral',
            icon: TrendingUp,
            color: 'from-emerald-500 to-teal-600',
            href: '/app/ranking'
          }
        ];

        setStats(memberStats);

        // Atividades recentes do clã - buscar do endpoint de gamificação
        try {
          const activitiesRes = await api.get('/gamification/activities?limit=10&type=LEVEL_UP');
          const pointsActivities = activitiesRes.data.activities || [];

          const recentActivities = [];

          // Adicionar atividades de pontos (de todos os membros)
          pointsActivities.forEach(activity => {
            const characterName = activity.character?.name || 'Personagem';
            const userName = activity.character?.user?.name || 'Membro';
            recentActivities.push({
              title: characterName,
              description: activity.description,
              time: getTimeAgo(activity.createdAt),
              icon: Sparkles,
              iconBg: activity.points > 0 ? 'from-green-500 to-emerald-600' : 'from-red-500 to-rose-600'
            });
          });

          setActivities(recentActivities);
        } catch (err) {
          console.error('Erro ao carregar atividades:', err);
          // Fallback se não conseguir carregar atividades
          setActivities([]);
        }
      } catch (err) {
        console.error('Erro ao carregar dashboard:', err);
        setError('Erro ao carregar dados da dashboard.');

        // Fallback para dados estáticos em caso de erro
        const fallbackStats = isAdmin ? [
          {
            name: 'Total de Personagens',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Sword,
            color: 'from-cyan-500 to-blue-600',
            href: '/app/characters'
          },
          {
            name: 'Eventos Ativos',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Calendar,
            color: 'from-purple-500 to-pink-600',
            href: '/app/events'
          },
          {
            name: 'Pontos Totais',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Trophy,
            color: 'from-amber-500 to-orange-600',
            href: '/app/ranking'
          },
          {
            name: 'Membros Ativos',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Users,
            color: 'from-emerald-500 to-teal-600',
            href: '/app/ranking'
          },
          {
            name: 'Recrutamentos Pendentes',
            value: '--',
            change: 'Carregando...',
            changeType: 'neutral',
            icon: Medal,
            color: 'from-rose-500 to-pink-600',
            href: '/app/admin/enlistments'
          }
        ] : [
          {
            name: 'Meus Personagens',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Sword,
            color: 'from-cyan-500 to-blue-600',
            href: '/app/characters'
          },
          {
            name: 'Eventos Participados',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Calendar,
            color: 'from-purple-500 to-pink-600',
            href: '/app/events'
          },
          {
            name: 'Meus Pontos',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: Trophy,
            color: 'from-amber-500 to-orange-600',
            href: '/app/ranking'
          },
          {
            name: 'Ranking',
            value: '--',
            change: 'Carregando...',
            changeType: 'positive',
            icon: TrendingUp,
            color: 'from-emerald-500 to-teal-600',
            href: '/app/ranking'
          }
        ];

        setStats(fallbackStats);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [isAdmin, user]);

  // Função auxiliar para calcular tempo decorrido
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'agora';
    if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`;
    return `${Math.floor(diffInMinutes / 1440)}d atrás`;
  };

  if (loading) {
    return <LoadingPage />;
  }
  if (error) {
    return <div className="text-center text-red-600 py-8">{error}</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center sm:text-left"
      >
        <div className="mb-4">
          <h1
            className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-3 animate-gradient">
            {isAdmin ? '⚡ Painel Administrativo' : `Bem-vindo, ${user?.name}! ✨`}
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl">
            {isAdmin
              ? 'Gerencie o clã, acompanhe estatísticas e administre membros, eventos e recursos.'
              : 'Gerencie seus personagens, participe de eventos épicos e conquiste sua posição no ranking do clã.'
            }
          </p>
        </div>
      </motion.div>

      {/* Botão Especial - Recap 2025 (Temporário até 05/01/2026) */}
      {showRecapButton && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 rounded-3xl blur-xl animate-pulse"></div>
          <Card variant="glass" padding="lg" className="relative overflow-hidden border-2 border-cyan-500/30">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-purple-500/10"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-cyan-500/50 animate-pulse">
                  <Film className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
                      Retrospectiva 2025
                    </h2>
                    <span className="px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold rounded-full animate-pulse">
                      NOVO
                    </span>
                  </div>
                  <p className="text-gray-300 text-lg mb-1">
                    Reviva sua jornada épica no Clã Revolution durante 2025!
                  </p>
                  <p className="text-gray-400 text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Disponível até 05/01/2026
                  </p>
                </div>
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={goToRecap}
                className="whitespace-nowrap group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Ver Minha Retrospectiva
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Botão Especial - Aniversário (Temporário até 31/01/2026) */}
      {showBirthdayButton && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative"
        >
          {/* Glow externo */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-purple-500/30 rounded-[2.5rem] blur-2xl animate-pulse" />

          <Card
            variant="glass"
            padding="lg"
            className="relative overflow-hidden border border-cyan-400/30 rounded-[2.5rem]"
          >
            {/* Backgrounds decorativos */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-purple-500/10" />
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-gradient-to-br from-purple-500/30 to-blue-500/30 rounded-full blur-3xl" />

            <div className="relative z-10 flex flex-col gap-8">
              {/* Cabeçalho */}
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-cyan-500/50 animate-pulse">
                  <PartyPopper className="w-12 h-12 text-white" />
                </div>

                <div className="text-center md:text-left">
                  <h2 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-2">
                    1 Ano de Revolution
                  </h2>
                  <p className="text-gray-200 text-lg md:text-xl font-semibold">
                    Parabéns a todos nós por essa jornada incrível!
                  </p>
                  <p className="text-gray-300 text-base mt-1 max-w-2xl">
                    Obrigado a cada membro que construiu a Revolution com dedicação,
                    amizade e espírito de equipe. Vocês fazem esse clã ser único!
                  </p>
                </div>
              </div>

              {/* Call to Action */}
              <div className="flex flex-col items-center gap-4">
                <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  Vamos comemorar juntos nos eventos especiais de aniversário!
                </p>

                <div className="flex flex-col md:flex-row gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() =>
                      (window.location.href =
                        "/app/events/e69692ef4e83e30.59863508008a18474023cf30")
                    }
                    className="whitespace-nowrap group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-xl shadow-cyan-500/40 hover:shadow-cyan-500/70 transition-all duration-300 px-8 py-6 text-lg rounded-2xl"
                  >
                    Evento 30/01
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() =>
                      (window.location.href =
                        "/app/events/e69692f211febc4.761021809403807daf82cacf")
                    }
                    className="whitespace-nowrap group bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-400 hover:to-blue-500 shadow-xl shadow-purple-500/40 hover:shadow-purple-500/70 transition-all duration-300 px-8 py-6 text-lg rounded-2xl"
                  >
                    Evento 31/01
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>

                <span className="text-sm text-gray-400 italic">
          Sua presença é o nosso maior presente. 💙
        </span>
              </div>
            </div>
          </Card>
        </motion.div>

      )}

      {/* Stats User Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-6 lg:grid-cols-4'}`}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={stat.href}>
                <Card
                  variant="glass"
                  padding="default"
                  hover
                  className="group relative overflow-hidden h-full"
                >
                  {/* Gradient glow */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>

                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-all duration-300`}>
                        <Icon className="w-7 h-7" />
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${stat.changeType === 'positive'
                          ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30'
                          : stat.changeType === 'neutral'
                            ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30'
                            : 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30'
                        }`}>
                        {stat.changeType === 'positive' ? '↗' : stat.changeType === 'neutral' ? '→' : '↘'} {stat.change}
                      </div>
                    </div>
                    <div>
                      <p
                        className="text-4xl font-black text-white mb-1 group-hover:scale-105 transition-transform duration-300">
                        {stat.value}
                      </p>
                      <p className="text-sm font-semibold text-gray-400 group-hover:text-gray-300 transition-colors">
                        {stat.name}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Stats Admin Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-6 lg:grid-cols-5`} hidden={!isAdmin}>
        {statsAdmin.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.name}
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: index * 0.1}}
            >
              <Link to={stat.href}>
                <Card
                  variant="glass"
                  padding="default"
                  hover
                  className="group relative overflow-hidden h-full"
                >
                  {/* Gradient glow */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>

                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-all duration-300`}>
                        <Icon className="w-7 h-7"/>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        stat.changeType === 'positive'
                          ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/30'
                          : stat.changeType === 'neutral'
                            ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30'
                            : 'bg-red-500/20 text-red-400 ring-1 ring-red-500/30'
                        }`}>
                        {stat.changeType === 'positive' ? '↗' : stat.changeType === 'neutral' ? '→' : '↘'} {stat.change}
                      </div>
                    </div>
                    <div>
                      <p
                        className="text-4xl font-black text-white mb-1 group-hover:scale-105 transition-transform duration-300">
                        {stat.value}
                      </p>
                      <p className="text-sm font-semibold text-gray-400 group-hover:text-gray-300 transition-colors">
                        {stat.name}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-3xl font-black text-white mb-6">Ações Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.name}
                to={action.href}
                className="group relative overflow-hidden rounded-2xl"
              >
                <Card variant="glass" padding="lg" className="h-full">
                  {/* Animated gradient background */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>

                  <div className="relative z-10">
                    <div
                      className={`w-16 h-16 bg-gradient-to-br ${action.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{action.name}</h3>
                    <p className="text-gray-300 mb-4">{action.description}</p>
                    <div
                      className="inline-flex items-center text-cyan-400 group-hover:text-cyan-300 font-semibold transition-colors duration-200">
                      <span className="text-sm">Começar</span>
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </motion.div>

      {/* Admin Actions */}
      {isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-3xl font-black text-white mb-6">Painel Administrativo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/app/admin/proof-review">
              <Card variant="glass" padding="lg" hover className="h-full group">
                <div
                  className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-600/10 opacity-100"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div
                        className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Revisar Provas</h3>
                      <p className="text-gray-300 text-sm mb-4">
                        Aprovar ou rejeitar provas de participação em eventos
                      </p>
                    </div>
                  </div>
                  <div
                    className="inline-flex items-center text-amber-400 group-hover:text-amber-300 font-semibold transition-colors duration-200">
                    <span className="text-sm">Abrir Revisões</span>
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </Card>
            </Link>

            <Link to="/app/admin">
              <Card variant="glass" padding="lg" hover className="h-full group">
                <div
                  className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-600/10 opacity-100"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div
                        className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform">
                        <Activity className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Painel Admin</h3>
                      <p className="text-gray-300 text-sm mb-4">
                        Gerenciar usuários, itens, relíquias e quests
                      </p>
                    </div>
                  </div>
                  <div
                    className="inline-flex items-center text-amber-400 group-hover:text-amber-300 font-semibold transition-colors duration-200">
                    <span className="text-sm">Acessar</span>
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </Card>
            </Link>

            <Link to="/app/admin/enlistments">
              <Card variant="glass" padding="lg" hover className="h-full group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-600/10 opacity-100"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div
                        className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform">
                        <Medal className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Gerenciar alistamentos</h3>
                      <p className="text-gray-300 text-sm mb-4">
                        Ver os últimos alistamentos
                      </p>
                    </div>
                  </div>
                  <div
                    className="inline-flex items-center text-purple-400 group-hover:text-purple-300 font-semibold transition-colors duration-200">
                    <span className="text-sm">Acessar</span>
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        </motion.div>
      )}

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card variant="glass" padding="lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-black text-white">
              Últimos UP
            </h2>
            {isAdmin && (
              <Link
                to="/app/activities"
                className="text-cyan-400 hover:text-cyan-300 font-semibold text-sm flex items-center gap-1 group"
              >
                Ver todas
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
              </Link>
            )}
          </div>

          <div className="space-y-4">
            {activities.length === 0 ? (
              <div className="text-gray-400 text-center py-12">
                <Activity className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-semibold">Nenhuma atividade recente encontrada</p>
              </div>
            ) : (
              activities.map((activity, idx) => {
                const Icon = activity.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="group"
                  >
                    <div
                      className="flex items-center p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white mr-4 bg-gradient-to-br ${activity.iconBg} shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-white mb-1">{activity.title}</p>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span>{activity.description}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {activity.time}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* See All Activities Link */}
          {activities.length > 0 && (
            <div className="mt-6 text-center" hidden>
              <Link to="/app/activities">
                <Button variant="primary" size="lg" fullWidth className="group">
                  <Activity className="w-5 h-5 mr-2" />
                  Ver todas as atividades
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </motion.div>
      {/* Admin Modals */}
      {isAdmin && (
        <AdminProofReviewModal
          isOpen={showProofReviewModal}
          onClose={() => setShowProofReviewModal(false)}
          onReviewComplete={() => {
            // Optionally refresh dashboard stats
            // Você pode chamar fetchDashboard() aqui se quiser atualizar após revisão
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;